// © Microsoft Corporation. All rights reserved.

import { SocialMedia, SocialMediaDefaultConfig } from "./SocialMediaConfig";
import { indexOf, uniqueId } from "lodash-es";

import { String } from "typescript-string-operations";
import { format } from "@microsoft/fast-web-utilities";
import { getAppEnvironment } from "@msnews/core";
import { getLocationOrigin } from "@msnews/isomorphic-util";
import { logger } from "@msnews/diagnostics";

/**
 * Document type being shared
 */
export enum SharedDocumentType {
    CMS = "0",
    SD = "5"
}

/**
 * Sharing details interface to build sharing items
 * @interface
 */
export interface SharingInfo {
    /**
     * CMS document id
     * @type {string}
     */
    documentId: string;

    /**
     * Document type
     * @type {SharedDocumentType}
     */
    documentType: SharedDocumentType;

    /**
     * Sharing url for the content
     * @type {string}
     */
    sharingUrl?: string;
}

/**
 * Helper for building sharing details
 * @class
 */
export class SharingHelper {
    /**
     * Base production url used in the sharing link
     */
    private static readonly SharingBaseUrlDefault: string = "http://a.msn.com/";

    /**
     * Path format used to build the sharing url
     */
    private static readonly shortUrlPathFormat: string = "{0}{1}/{2}/{3}";

    /**
     * List the available options that should be mapped to prod url
     */
    private static readonly SharingProductionHosts: string = "bing.com|www.bing.com|preview.msn.com|www.msn.com";

    /**
     * String used to map the AppId number to single char identifier required in the sharing url
     */
    private static readonly Base36AppIdString: string = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    /**
     * Path format used to append querystring to the sharing url
     */
    private static readonly appendQueryStringFormat: string = "{0}?ocid={1}";

    /**
     * List of available vertical keys to be mapped.
     * The order matter here as the AppId to be used is based on the array index of the key
     */
    private static readonly AppKeys: string[] = ["finance",
        "news",
        "sports",
        "travel",
        "weather",
        "health",
        "foodanddrink",
        "entertainment",
        "autos",
        "video",
        "tv",
        "music",
        "movies",
        "games",
        "lifestyle",
        "kids",
        "sample",
        "asia",
        "africa",
        "australasia",
        "europe",
        "latinamerica",
        "middleeast",
        "northamerica"];

    /**
     *  Builds the short url that links to the article to be shared
     * @param sharingInfo The sharing data based on SharingInfo interface
     * @param locale The locale
     * @returns String with the short url to be shared
     */
    public static shortUrlBuilder(sharingInfo: SharingInfo, locale: string): string {
        const appId = this.getAppId();
        if (!appId) {
            return null;
        }

        const baseUrl = this.getSharingUrlBase(sharingInfo.sharingUrl);

        return baseUrl + format(this.shortUrlPathFormat, sharingInfo.documentType, appId, locale, sharingInfo.documentId);
    }

    /**
     * Gets the base36 AppId that is mapped from the vertical key identifier
     * @returns The mapped AppId
     */
    public static getAppId(): string {
        const { HostPage } = getAppEnvironment();
        const verticalKey: string = ((HostPage && HostPage.verticalKey) || "news").toLowerCase();
        let appId = indexOf(this.AppKeys, verticalKey);
        if (appId === -1 || appId >= this.Base36AppIdString.length) {
            appId = 1;
        }
        const base36Id = this.Base36AppIdString.substr(appId, 1);
        return base36Id;
    }

    /**
     * Gets the base url to be used in the shared link
     * @param articleUrl The full article url
     * @returns The base url to be used in the shared link
     */
    public static getSharingUrlBase(articleUrl: string): string {
        const url = new URL(articleUrl, getLocationOrigin());

        const host = url.host;
        const currentBaseUrl = url.origin + "/";

        const splitUrls = this.SharingProductionHosts.split("|");

        const baseUrl = splitUrls.indexOf(host) > -1 ? this.SharingBaseUrlDefault : currentBaseUrl;

        return baseUrl;
    }

    /**
     * Strips off query string params
     * @param url Input url
     * @returns The plain url
     */
    public static getUrlWithoutQueryParams(url: string): string {
        if (url && url.length > 0) {
            return url.split("?")[0];
        }

        return null;
    }

    /**
     * Retrieves the URL for sharing with Social Media.
     * @param locationHref current window.location.href from requestContext
     * @param medias social media to be included
     * @param contentUrl The url of the content to be shared
     * @param contentTitle The title of the content
     * @param emailBody The body text to be used in the email
     * @param ocid The ocid to be included in the shared link
     * @returns The share url
     */
    public static getSocialMediaShareUrl(locationHref: string, media: string, contentUrl: string, contentTitle: string, emailBody?: string, ocid?: string, overrideShareUrl?: string): string {
        if (!locationHref || !media || !contentUrl || !contentTitle) {
            logger.logError(`SharingHelper: getSocialMediaShareUrl: Parameter missing: locationHref=${locationHref}, media=${media}, contentUrl=${contentUrl}, contentTitle=${contentTitle}`);
            return;
        }

        const socialMediaItemConfig = SocialMediaDefaultConfig[media];
        if (!socialMediaItemConfig) {
            logger.logError(`SharingHelper: getSocialMediaShareUrl: Cannot find media type: ${socialMediaItemConfig}`);
            return "";
        }

        let shareUrl = overrideShareUrl || socialMediaItemConfig.url;
        if (media === SocialMedia.Skype) {
            shareUrl = shareUrl.replace("{flowid}", uniqueId());
        }

        if (ocid) {
            contentUrl = format(this.appendQueryStringFormat, contentUrl, ocid);
        }

        if (media === SocialMedia.Email && emailBody) {
            emailBody = format(emailBody, contentTitle, contentUrl);
        }

        shareUrl = String.Format(shareUrl, {
            url: encodeURIComponent(contentUrl),
            title: encodeURIComponent(contentTitle),
            body: emailBody ? encodeURIComponent(emailBody): "",
            refererUrl: locationHref
        });

        return shareUrl;
    }

    /**
     * Opens the social media sharing page in a new window
     * @param event Mouse event
     * @param shareUrl The complete url to be shared
     * @param options The features optional parameter in window.open
     */
    public static openNewWindow = (event: React.MouseEvent, shareUrl: string, options?: any): void => {
        const defaultOptions = { toolbar: 0, status: 0, resizable: 1, scrollbars: 1 };
        const finalOptions = Object.entries({ ...defaultOptions, ...options })
            .map(entry => entry.join("="))
            .join(",");

        window.open(shareUrl, null, finalOptions);
    };
}